import { atom } from "jotai";
import { atomWithReset } from "jotai/utils";
import { Batch } from "../../types/batch";
import { startGenerationTimer } from "../../utils/generation-timing";

// Type for callback functions
type MaybeAsyncFn<T, G> = ((arg: T) => G) | ((arg: T) => Promise<G>);

// Base atoms for batch state
export const batchesAtom = atomWithReset<Batch[]>([]);
export const onStartCallbacksAtom = atom<Record<number, MaybeAsyncFn<Batch, void>>>({});
export const onCompleteCallbacksAtom = atom<Record<number, MaybeAsyncFn<Batch, void>>>({});

// Derived atom for setting batches
export const setBatchesAtom = atom(null, (_, set, batches: Batch[]) => {
  set(batchesAtom, batches);
});

// Derived atom for adding a single batch the user just created. Only this path starts
// the e2e generation timer: upserts load batches other tabs or users created, and
// timing those would measure how long this tab watched rather than generation latency.
export const addBatchAtom = atom(null, (get, set, batch: Batch) => {
  const currentBatches = get(batchesAtom);
  startGenerationTimer(batch);
  set(batchesAtom, [...currentBatches, batch]);
});

// Derived atom for updating an existing batch
export const updateBatchAtom = atom(null, (get, set, updatedBatch: Batch) => {
  const currentBatches = get(batchesAtom);
  const index = currentBatches.findIndex((batch) => batch.id === updatedBatch.id);

  if (index !== -1) {
    const newBatches = [...currentBatches];
    newBatches[index] = updatedBatch;
    set(batchesAtom, newBatches);
  }
});

// Derived atom for upserting a single batch (update if exists, add if not)
export const upsertBatchAtom = atom(null, (get, set, batchToUpsert: Batch) => {
  const currentBatches = get(batchesAtom);
  const exists = currentBatches.some((batch) => batch.id === batchToUpsert.id);

  if (exists) {
    // Update existing batch
    const index = currentBatches.findIndex((batch) => batch.id === batchToUpsert.id);
    const newBatches = [...currentBatches];
    newBatches[index] = batchToUpsert;
    set(batchesAtom, newBatches);
  } else {
    // Add new batch
    set(batchesAtom, [...currentBatches, batchToUpsert]);
  }
});

// Derived atom for upserting multiple batches
export const upsertBatchesAtom = atom(null, (get, set, batchesToUpsert: Batch[]) => {
  batchesToUpsert.forEach((batch) => {
    const currentBatches = get(batchesAtom);
    const exists = currentBatches.some((existingBatch) => existingBatch.id === batch.id);

    if (exists) {
      // Update existing batch
      const index = currentBatches.findIndex((existingBatch) => existingBatch.id === batch.id);
      const newBatches = [...currentBatches];
      newBatches[index] = batch;
      set(batchesAtom, newBatches);
    } else {
      // Add new batch
      const currentBatchesAfterUpdate = get(batchesAtom);
      set(batchesAtom, [...currentBatchesAfterUpdate, batch]);
    }
  });
});

// Derived atom for subscribing to batch callbacks
export const subscribeToBatchAtom = atom(
  null,
  (
    get,
    set,
    {
      batch,
      onStart,
      onComplete
    }: {
      batch: Batch;
      onStart: MaybeAsyncFn<Batch, void>;
      onComplete: MaybeAsyncFn<Batch, void>;
    }
  ) => {
    const currentStartCallbacks = get(onStartCallbacksAtom);
    const currentCompleteCallbacks = get(onCompleteCallbacksAtom);

    set(onStartCallbacksAtom, {
      ...currentStartCallbacks,
      [batch.id]: onStart
    });

    set(onCompleteCallbacksAtom, {
      ...currentCompleteCallbacks,
      [batch.id]: onComplete
    });
  }
);

// Derived atom for getting a specific batch by ID
export const getBatchByIdAtom = atom((get) => (id: number) => {
  const batches = get(batchesAtom);
  return batches.find((batch) => batch.id === id);
});

// Derived atom for getting batches count
export const batchesCountAtom = atom((get) => {
  const batches = get(batchesAtom);
  return batches.length;
});

// Derived atom for getting pending batches (you can customize the condition)
export const pendingBatchesAtom = atom((get) => {
  const batches = get(batchesAtom);
  return batches.filter(
    (batch) => batch.status === "pending" || batch.status === "processing" || batch.status === "created"
  );
});

// Derived atom for getting completed batches
export const completedBatchesAtom = atom((get) => {
  const batches = get(batchesAtom);
  return batches.filter((batch) => batch.status === "completed" || batch.status === "partial_success");
});

// Derived atom for getting failed batches
export const failedBatchesAtom = atom((get) => {
  const batches = get(batchesAtom);
  return batches.filter((batch) => batch.status === "failed");
});

// Derived atom for resetting all batch state
export const resetBatchStateAtom = atom(null, (_, set) => {
  set(batchesAtom, []);
  set(onStartCallbacksAtom, {});
  set(onCompleteCallbacksAtom, {});
});
